import React, { useEffect, useMemo } from "react";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";
import {
  checkAppRedirects,
  isBypassPath,
  isChannelEntryInPlatform,
  isOnboardingPath,
} from "utils/routes";
import { useSelector } from "react-redux";
import {
  BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH,
  CONTENT_WRAPPER_ID,
  IS_WHOP,
  QUICK_ONBOARDING_PATH,
} from "common/constants";
import { IS_RESTRICTED_COUNTRY } from "utils/hooks/useRestrictedCountries";
import { useHideBillingButtons } from "features/settings/common/hooks";
import { skipToken } from "@reduxjs/toolkit/query";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { useConsumeSearchParams } from "utils/hooks/useSearchParams";
import {
  useBaseQueries,
  useHotjar,
  useBottomNavigator,
  useIsOnboarding,
  useScrollToTop,
  useSentryUser,
  usePollClientsList,
} from "utils/hooks/misc";
import { AuthUser } from "@shared/types";
import { didUserSkipOnboarding } from "features/onboarding/common/utils";
import { sharedLib } from "@shared/services/shared.service";
import {
  selectWorkspace,
  selectWorkspaceInitialized,
} from "@shared/slices/common.slice";
import { useGetUser, useTelegramWebAppData } from "@shared/utils/hooks/misc";
import { useInitAdminGroupsSelect } from "features/admin/hooks";
import useWorkspace from "utils/hooks/useWorkspace";
import useLogout from "utils/hooks/useLogout.ts";
import useQuery from "utils/hooks/useQuery.ts";
import ClientsDataProvider from "@shared/features/common/ClientsDataProvider.tsx";
import SymbolsMetadataProvider from "@shared/features/common/SymbolsMetadataProvider.tsx";
import ExchangesMetadataProvider from "@shared/features/common/ExchangesMetadataProvider.tsx";
import AccountFormProvider from "@shared/features/common/AccountFormProvider.tsx";
import PageWrapper from "common/Home/PageWrapper/PageWrapper.tsx";
import PrivateRoute from "common/Routes/PrivateRoute.tsx";
import CreateAccountDrawer from "features/portfolio/CreateAccount/CreateAccountDrawer.tsx";
import styled, { useTheme } from "styled-components";
import QueriesError from "common/Empty/QueriesError.tsx";
import * as Sentry from "@sentry/react";
import Drawers from "common/Home/General/Drawers.tsx";
import PrecisionsDataProvider from "@shared/features/common/PrecisionsDataProvider.tsx";
import BottomNavigatorPages from "common/Home/MobileHome/BottomNavigatorPages.tsx";
import RedirectWithQuery from "common/Routes/RedirectWithQuery.tsx";
import { lazyLoad } from "utils/lazyLoad.tsx";
import { PAGES_DATA } from "common/Home/General/PagesData.tsx";
import SuspenseLoading from "common/Home/General/SuspenseLoading.tsx";
import LeaveReviewModal from "features/misc/LeaveReviewModal.tsx";
import AcceptTermsModal from "features/misc/AcceptTermsModal.tsx";
import TelegramNotificationsModal from "features/misc/TelegramNotificationsModal.tsx";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import { isChannelEntry } from "features/telegram/utils.ts";
import { useConsumeDiscordLinkCode } from "features/auth/useDiscordLink";

const QuickOnboarding = lazyLoad(
  () => import("features/quickOnboarding/QuickOnboarding")
);

const Home = () => {
  const theme = useTheme();
  const workspace = useSelector(selectWorkspace);
  const workspaceInitialized = useSelector(selectWorkspaceInitialized);
  const token = useSelector(sharedLib.selectors.authentication.selectToken);
  const [logout, { isLoading: isLoadingLogout }] = useLogout();
  const status = useGetUser();
  const { isSuccess, isError } = status;
  const isOnboarding = useIsOnboarding();
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const location = useLocation();
  // Connecting an account from the bot form opens this.
  const showCreateAccountDrawer =
    !isOnboarding || isChannelEntryInPlatform(location);
  sharedLib.sharedApi.common.useGetAvailableIconAssetsQuery(
    !token || isOnboarding ? skipToken : undefined
  );
  useScrollToTop();

  const getUserError = (isSuccess && !user) || isError;
  const shouldLogout = getUserError && !isLoadingLogout && !IS_WHOP;
  useEffect(() => {
    if (shouldLogout) {
      logout().catch(() => {});
    }
  }, [shouldLogout]);

  const content = useMemo(() => {
    return (
      user &&
      workspaceInitialized && (
        <PrecisionsDataProvider>
          <ClientsDataProvider>
            <SymbolsMetadataProvider>
              <ExchangesMetadataProvider>
                <AccountFormProvider>
                  <PageWrapper user={user}>
                    <Content user={user} key={workspace} />
                    {showCreateAccountDrawer && <CreateAccountDrawer />}
                  </PageWrapper>
                </AccountFormProvider>
              </ExchangesMetadataProvider>
            </SymbolsMetadataProvider>
          </ClientsDataProvider>
        </PrecisionsDataProvider>
      )
    );
  }, [showCreateAccountDrawer, user, workspace, workspaceInitialized]);

  return (
    <LoadingSpinnerOverlay
      isLoading={!user || !workspaceInitialized || isLoadingLogout}
      spinnerSize="large"
      hideContentWhileLoading={true}
      backgroundColor={theme.colors.layoutBgColor}
    >
      {content}
    </LoadingSpinnerOverlay>
  );
};

export default Home;

interface ContentProps {
  user: AuthUser;
}

const Content = ({ user }: ContentProps) => {
  const query = useQuery();
  const location = useLocation();
  useConsumeSearchParams();
  useConsumeDiscordLinkCode();
  const [finishOnboarding] =
    sharedLib.sharedApi.authentication.useFinishOnboardingMutation();
  const isOnboarding = useIsOnboarding();
  const skipOnboarding = didUserSkipOnboarding(user.id);
  useBaseQueries();
  useHotjar();
  useSentryUser();
  useInitAdminGroupsSelect();
  const { successContextHolder } = usePollClientsList();
  const { getHomePath, getValidWorkspacesByPath } = useWorkspace();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const workspace = useSelector(selectWorkspace);
  const { telegramStartValue } = useTelegramWebAppData();
  const history = useHistory();
  const hideBilling = useHideBillingButtons();

  const redirect = checkAppRedirects(
    user,
    location,
    query,
    getValidWorkspacesByPath,
    getHomePath,
    telegramStartValue,
    workspace
  );

  useEffect(() => {
    if (
      user.is_onboarding &&
      !isOnboardingPath(location) &&
      !isBypassPath(location) &&
      !redirect &&
      // From a channel post's button, onboarding ends with their first bot.
      !isChannelEntry()
    ) {
      finishOnboarding();
      // Without a subscription (an account deleted and made again gets no
      // second trial) there's nothing to set up yet: the plans come first.
      if (user.isExpired() && !hideBilling && !IS_RESTRICTED_COUNTRY)
        history.replace(BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH);
    }
  }, [isOnboarding, skipOnboarding]);

  const switchItems = useMemo(() => {
    return Object.entries(PAGES_DATA).map(([pageKey, pageData]) => {
      if (
        pageData.isMobileOnly ||
        (isBottomNavigator &&
          (pageData.isBottomNavigatorScreen || pageData.isDesktopOnly))
      )
        return;
      return (
        <PrivateRoute
          key={pageKey}
          path={pageData.path}
          component={pageData.component}
        />
      );
    });
  }, [isBottomNavigator]);

  if (redirect) return redirect;

  return (
    <ContentWrapper id={CONTENT_WRAPPER_ID}>
      <Sentry.ErrorBoundary
        fallback={<QueriesError isGeneralPageError={true} />}
      >
        <SuspenseLoading>
          {isBottomNavigator && <BottomNavigatorPages />}
          <Switch>
            {isBottomNavigator && (
              <PrivateRoute
                path={QUICK_ONBOARDING_PATH}
                component={QuickOnboarding}
              />
            )}
            {switchItems}
            <Route exact path="/">
              <RedirectWithQuery to={getHomePath()} />
            </Route>
          </Switch>
          <LeaveReviewModal />
          <AcceptTermsModal />
          <Drawers />
          {IS_TELEGRAM_WEB_APP && <TelegramNotificationsModal />}
          {successContextHolder}
        </SuspenseLoading>
      </Sentry.ErrorBoundary>
    </ContentWrapper>
  );
};

const ContentWrapper = styled.div`
  position: relative;
  min-height: 100%;
  display: flex;
  flex-direction: column;
`;
